import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Card, Color, Switch, message } from '@bb/elements-web';
import { useMutation } from '@tanstack/react-query';
import { updateNotificationSettings } from 'api/SupraLockboxController';
import { useSupraAccessDetailsPageContext } from '../SupraAccessDetailsPageContext';

type NotificationSettings = {
  showingStarted: boolean;
  lockboxAssignmentReminder: boolean;
};

const NotificationsCard: React.FC = () => {
  const { userId, subscription, silentRefetch } =
    useSupraAccessDetailsPageContext();

  const [settings, setSettings] = useState<NotificationSettings>({
    showingStarted: false,
    lockboxAssignmentReminder: false,
  });

  useEffect(() => {
    if (subscription) {
      setSettings({
        showingStarted:
          subscription.notificationSettings?.showingStarted ?? false,
        lockboxAssignmentReminder:
          subscription.notificationSettings?.lockboxAssignmentReminder ?? false,
      });
    }
  }, [subscription]);

  const mutation = useMutation({
    mutationFn: (next: NotificationSettings) =>
      updateNotificationSettings({ userId, ...next }),
    onMutate: (next: NotificationSettings) => {
      const previous = settings;
      setSettings(next);
      return { previous };
    },
    onError: (_error, _next, context) => {
      message.error('Failed to update notification preferences.');
      if (context) {
        setSettings(context.previous);
      }
    },
    onSuccess: () => {
      silentRefetch();
    },
  });

  const handleToggle = (key: keyof NotificationSettings, checked: boolean) => {
    mutation.mutate({ ...settings, [key]: checked });
  };

  return (
    <StyledCard title="Notifications" dataCy="supra-notifications-card">
      <Row>
        <RowLabel>Notify me of lockbox opening to start showing</RowLabel>
        <Switch
          checked={settings.showingStarted}
          disabled={mutation.isLoading}
          onChange={checked => handleToggle('showingStarted', checked)}
          dataCy="supra-notifications-showing-started"
        />
      </Row>
      <Row>
        <RowLabel>Notify me about lockbox assignment reminders</RowLabel>
        <Switch
          checked={settings.lockboxAssignmentReminder}
          disabled={mutation.isLoading}
          onChange={checked =>
            handleToggle('lockboxAssignmentReminder', checked)
          }
          dataCy="supra-notifications-lockbox-reminder"
        />
      </Row>
    </StyledCard>
  );
};

export default NotificationsCard;

const StyledCard = styled(Card)`
  .ant-card-head-title {
    font-size: 20px;
    font-weight: 600;
    color: ${Color.PRIMARY_LIGHT_TEXT};
  }
  .ant-card-head {
    border-bottom: none;
    padding-bottom: 0;
    min-height: auto;
  }
  .ant-card-body {
    padding-top: 8px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  &:not(:last-child) {
    border-bottom: 1px solid ${Color.TRANSPARENT};
  }
`;

const RowLabel = styled.div`
  font-size: 16px;
  color: ${Color.PRIMARY_LIGHT_TEXT};
`;
